import { useDispatch, useSelector } from 'react-redux'
import './CertificateList.scss'
import { type RootState } from '../../../Store/Store'
import { TextInput } from '../../../Components/TextInput/TextInput'
import { useEffect, useMemo, useState } from 'react'
import { useLocalization } from '../../../Hooks/useLocalization'
import {
  certificateStatuses,
  setIsCertificatesRequested,
  certificateStatusesExpired,
  setIsCertificateTypesRequested,
  setIsCertificateInstancesRequested,
  certificateStatusesNotValidYet,
} from '../../../Store/DataSlice'
import { Button } from '../../../Components/Button/Button'
import { setDestinationView } from '../../../Store/InterfaceSlice'
import { CertificateList } from '../CertificateList'
import { sortResultString } from '../../../Utils/Arrays'
import { SingleSelect } from '../../../Components/SingleSelect/SingleSelect'

const localizationSection = 'certificate'
const statusFilterAnyStatus = 'any-status'
const certificateTypeAnyType = 'any-certificate-type'
const certificateInstanceAnyType = 'any-certificate-instance'

export function CertList() {
  const dispatch = useDispatch()
  const localization = useLocalization(localizationSection)
  const certificateStatusesLocalization = useLocalization(
    'certificate-statuses'
  )
  const isLoggedIn = useSelector(
    (state: RootState) => !!state.authentication.user
  )

  // CertificateTypes
  const isCertificateTypesRequested = useSelector(
    (state: RootState) => state.data.isCertificateTypesRequested
  )
  useEffect(() => {
    if (!isCertificateTypesRequested)
      dispatch(setIsCertificateTypesRequested(true))
  }, [isCertificateTypesRequested, dispatch])

  const allCertificateTypes = useSelector(
    (state: RootState) => state.data.certificateTypes
  )

  // CertificateInstances
  const isCertificateInstancesRequested = useSelector(
    (state: RootState) => state.data.isCertificateInstancesRequested
  )
  useEffect(() => {
    if (!isCertificateInstancesRequested)
      dispatch(setIsCertificateInstancesRequested(true))
  }, [isCertificateInstancesRequested, dispatch])

  const allCertificateInstances = useSelector(
    (state: RootState) => state.data.certificateInstances
  )
  const allCertificateInstancesSorted = useMemo(
    () =>
      [...(allCertificateInstances ?? [])].sort((a, b) =>
        a.name.localeCompare(b.name)
      ),
    [allCertificateInstances]
  )

  // certificates
  const isCertificatesRequested = useSelector(
    (state: RootState) => state.data.isCertificatesRequested
  )
  useEffect(() => {
    if (!isCertificatesRequested) dispatch(setIsCertificatesRequested(true))
  }, [isCertificatesRequested, dispatch])
  const allCertificates = useSelector(
    (state: RootState) => state.data.enrichedCertificates
  )

  const [filterCompanyPostcode, setFilterCompanyPostcode] = useState('')
  const [filterCompany, setFilterCompany] = useState('')
  const [filterCompanyVat, setFilterCompanyVat] = useState('')
  const [filterScope, setFilterScope] = useState('')
  const [filterCertificateType, setFilterCertificateType] = useState(
    certificateTypeAnyType
  )
  const [filterStatus, setFilterStatus] = useState(statusFilterAnyStatus)
  const [filterCertificateInstance, setFilterCertificateInstance] = useState(
    certificateInstanceAnyType
  )
  const [filterCertificateNumber, setFilterCertificateNumber] = useState('')

  const filteredCertificates = useMemo(() => {
    const splitPostcodes =
      filterCompanyPostcode != null
        ? filterCompanyPostcode.split(/[^a-z0-9]+/gi)
        : []
    return allCertificates
      .filter((c) => {
        if (
          filterCompanyPostcode &&
          !splitPostcodes.some((fcp) => c.companyPostcodes.includes(fcp))
        )
          return false
        if (
          filterCompany &&
          !c.companyNames.toLowerCase().includes(filterCompany.toLowerCase())
        )
          return false
        if (
          filterCompanyVat &&
          !c.companyVatNumbers
            .toLowerCase()
            .includes(filterCompanyVat.toLowerCase())
        )
          return false
        if (
          filterScope &&
          !c.scope.toLowerCase().includes(filterScope.toLowerCase())
        )
          return false
        if (
          filterCertificateType !== certificateTypeAnyType &&
          c.type !== filterCertificateType
        )
          return false
        if (
          filterStatus !== statusFilterAnyStatus &&
          c.calculatedStatus !== filterStatus
        )
          return false
        if (
          filterCertificateInstance !== certificateInstanceAnyType &&
          c.certificateInstanceVatNumber !== filterCertificateInstance
        )
          return false
        if (
          filterCertificateNumber &&
          !c.certificateNumber
            .toLowerCase()
            .includes(filterCertificateNumber.toLowerCase())
        )
          return false
        return true
      })
      .sort((a, b) =>
        a.issueDate !== b.issueDate
          ? b.issueDate - a.issueDate
          : sortResultString(a.certificateNumber, b.certificateNumber)
      )
  }, [
    filterCompanyPostcode,
    allCertificates,
    filterCompany,
    filterCompanyVat,
    filterScope,
    filterCertificateType,
    filterStatus,
    filterCertificateInstance,
    filterCertificateNumber,
  ])

  return (
    <div className="certificate-list">
      {isLoggedIn ? (
        <div className="button-row">
          <Button
            importance={'primary'}
            text={localization.text('add-company', 'Voeg certificaat toe')}
            onClick={() =>
              dispatch(
                setDestinationView({
                  destinationView: 'certificate-add',
                })
              )
            }
          />
        </div>
      ) : null}
      <div className="filters">
        <div className="filter-field">
          <TextInput
            label={localization.text(
              'filter-company-input-label',
              'Onderneming'
            )}
            helperText={localization.text(
              'filter-company-input-helper',
              'Geef (een deel van) de naam van de onderneming in'
            )}
            value={filterCompany}
            onChange={setFilterCompany}
          />
        </div>
        <div className="filter-field">
          <TextInput
            label={localization.text(
              'filter-vat-number-input-label',
              'Ondernemingsnummer'
            )}
            helperText={localization.text(
              'filter-vat-number-input-helper',
              'Geef (een deel van) de het ondernemingsnummer in'
            )}
            value={filterCompanyVat}
            onChange={setFilterCompanyVat}
          />
        </div>
        <div className="filter-field">
          <SingleSelect
            label={localization.text(
              'filter-certificate-type-input-label',
              'Certificaat type'
            )}
            helperText={localization.text(
              'filter-certificate-type-input-helper',
              'Kies een certificaat type'
            )}
            items={[
              certificateTypeAnyType,
              ...allCertificateTypes.map((ct) => ct.name).sort(),
            ].map((certType) => ({
              label:
                certType === certificateTypeAnyType
                  ? localization.text(certType, certType)
                  : certType,
              value: certType,
            }))}
            value={filterCertificateType}
            onChange={setFilterCertificateType}
            isRequired={true}
          />
        </div>
        <div className="filter-field">
          <SingleSelect
            label={localization.text('filter-status-input-label', 'Status')}
            helperText={localization.text(
              'filter-status-input-helper',
              'Kies een status'
            )}
            items={[
              statusFilterAnyStatus,
              certificateStatusesExpired,
              certificateStatusesNotValidYet,
              ...certificateStatuses,
            ].map((status) => ({
              label: certificateStatusesLocalization.text(status, status),
              value: status,
            }))}
            value={filterStatus}
            onChange={setFilterStatus}
            isRequired={true}
          />
        </div>
        <div className="filter-field">
          <TextInput
            label={localization.text('filter-scope-input-label', 'Scope')}
            helperText={localization.text(
              'filter-scope-input-helper',
              'Geef (een deel van) de scope in'
            )}
            value={filterScope}
            onChange={setFilterScope}
          />
        </div>
        <div className="filter-field">
          <TextInput
            label={localization.text(
              'filter-company-postcodes-input-label',
              'Postcode'
            )}
            helperText={localization.text(
              'filter-company-postcodes-input-helper',
              'Geef komma gescheiden postcodes voor ondernemingen in'
            )}
            value={filterCompanyPostcode}
            onChange={setFilterCompanyPostcode}
          />
        </div>
        <div className="filter-field">
          <SingleSelect
            label={localization.text(
              'filter-certificate-instance-input-label',
              'Certificatie instantie'
            )}
            helperText={localization.text(
              'filter-certificate-instance-input-helper',
              'Kies een certificaat instantie'
            )}
            items={[
              certificateInstanceAnyType,
              ...allCertificateInstancesSorted.map((ct) => ct.vatNumber),
            ].map((certificateInstanceVatNumber) => ({
              label:
                certificateInstanceVatNumber === certificateInstanceAnyType
                  ? localization.text(
                      certificateInstanceVatNumber,
                      certificateInstanceVatNumber
                    )
                  : allCertificateInstances.find(
                      (ci) => ci.vatNumber === certificateInstanceVatNumber
                    )?.name ?? certificateInstanceVatNumber,
              value: certificateInstanceVatNumber,
            }))}
            value={filterCertificateInstance}
            onChange={setFilterCertificateInstance}
            isRequired={true}
          />
        </div>
        <div className="filter-field">
          <TextInput
            label={localization.text(
              'filter-certificate-number-input-label',
              'Certificaat nummer'
            )}
            helperText={localization.text(
              'filter-certificate-number-input-helper',
              'Geef (een deel van) het certificaat nummer in'
            )}
            value={filterCertificateNumber}
            onChange={setFilterCertificateNumber}
          />
        </div>
      </div>
      <CertificateList
        certificates={filteredCertificates}
        isLoading={allCertificates.length === 0}
      />
    </div>
  )
}
